<template>
    <div class="message-bubbles">
        <div
            v-for="(message, index) in messages"
            :key="index"
            class="message"
            :class="{ 'customer-message': message.type === 'customer' || message.type === 'typing' }"
        >
            <div
                v-if="message.type === 'typing'"
                class="typing-indicator"
            >
                <div class="typing-dots">
                    <span></span>
                    <span></span>
                    <span></span>
                </div>
            </div>
            <div
                v-else
                class="message-bubble"
            >
                <div
                    v-if="message.html"
                    v-html="message.text"
                />
                <template v-else>
                    {{ message.text }}
                    <a
                        v-if="message.link"
                        :href="message.link.url"
                        class="message-link"
                        @click="handleLinkClick($event, message.link)"
                    >
                        {{ message.link.text }}
                    </a>
                </template>
            </div>
            <div
                v-if="message.time && message.type !== 'typing'"
                class="message-time"
            >
                {{ message.time }}
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'DemoBubbles',

    props: {
        messages: {
            type: Array,
            required: true,
            validator: (messages) => {
                return messages.every(message =>
                    ['company', 'customer', 'typing'].includes(message.type) &&
                    (message.type === 'typing' || message.text)
                );
            }
        },
    },

    methods: {
        handleLinkClick(event, link) {
            if (link.preventDefault) {
                event.preventDefault();
            }
            if (link.callback) {
                link.callback(event);
            }
        }
    }
}
</script>

<style lang="scss" scoped>
.message-bubbles {
    --company-color: #E3F2FD;
    --customer-color: #C8E6C9;

    display: flex;
    flex-direction: column;
    gap: 0.5rem;

    .message {
        display: flex;
        flex-direction: column;
        align-items: flex-end;

        &.customer-message {
            align-items: flex-start;
        }

        .message-bubble {
            position: relative;
            max-width: min(450px, 85%);
            background-color: var(--company-color);
            padding: 0.75rem 1rem;
            border-radius: 1.5rem 1.5rem 0.25rem 1.5rem;
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            font-size: 0.875rem;
            line-height: 1.4;
            text-align: left;
            word-wrap: break-word;

            &::after {
                content: '';
                position: absolute;
                right: -0.5rem;
                bottom: 0;
                width: 1rem;
                height: 1rem;
                background: var(--company-color);
                clip-path: polygon(0 0, 100% 100%, 0 100%);
                border-bottom-right-radius: 0.25rem;
                z-index: 1;
            }

            .message-link {
                color: var(--v-primary-base);
                text-decoration: none;
                word-break: break-all;
                display: block;
                margin-top: 0.5rem;

                &:hover {
                    text-decoration: underline;
                }
            }
        }

        &.customer-message .message-bubble {
            background-color: var(--customer-color);
            border-radius: 1.5rem 1.5rem 1.5rem 0.25rem;

            &::after {
                left: -0.5rem;
                right: auto;
                background: var(--customer-color);
                clip-path: polygon(100% 0, 100% 100%, 0 100%);
                border-bottom-right-radius: 0;
                border-bottom-left-radius: 0.25rem;
            }
        }

        .message-time {
            font-size: 0.75rem;
            color: #666;
            margin-top: 0.25rem;
            margin-left: 0.5rem;
            margin-right: 0.5rem;
        }

        &.customer-message .message-time {
            margin-left: 0.5rem;
            margin-right: 0;
        }
    }

    .typing-indicator {
        background: #e5e5ea;
        border-radius: 18px;
        padding: 0.8rem 1rem;
        margin-bottom: 0.5rem;
        animation: fadeIn 0.3s ease-in;

        .typing-dots {
            display: flex;
            align-items: center;
            gap: 0.3rem;

            span {
                width: 6px;
                height: 6px;
                border-radius: 50%;
                background: #999;
                animation: typingDot 0.6s infinite ease-in-out;

                &:nth-child(1) {
                    animation-delay: 0s;
                }

                &:nth-child(2) {
                    animation-delay: 0.2s;
                }

                &:nth-child(3) {
                    animation-delay: 0.4s;
                }
            }
        }
    }
}

@keyframes fadeIn {
    from {
        opacity: 0;
        transform: translateY(10px);
    }

    to {
        opacity: 1;
        transform: translateY(0);
    }
}

@keyframes typingDot {

    0%,
    60%,
    100% {
        opacity: 0.5;
        transform: scale(1);
    }

    30% {
        opacity: 1;
        transform: scale(1.2);
    }
}
</style>